import { createSlice, nanoid } from "@reduxjs/toolkit";
import { loadLS, ZRAPOR_KEYS } from "../../utils/storage";

// Reports — each report is a card on the page
// Shape: { id, date, kasiyerId, kasaId, raporNo, toplamTutar, musteriSayisi,
//          nakit, hediyeKart,
//          visa: [{id, bankaId, tutar}],
//          giderler: [{id, kategoriId, tutar, aciklama}],
//          borclar: [{id, musteriId, tutar, aciklama}],
//          status: 'open'|'closed', createdAt, closedAt }
//
// Daily extras (per date, global — not per report):
//   dailyExtras[date] = { bozukPara, gecisBorc, kasaSefOdemeler: [{id, aciklama, tutar}] }

const persistedReports = loadLS(ZRAPOR_KEYS.reports, null);
const persistedClosure = loadLS(ZRAPOR_KEYS.dailyClosure, null);
const persistedExtras = loadLS(ZRAPOR_KEYS.dailyExtras, null);

const initialState = {
  reports: Array.isArray(persistedReports) ? persistedReports : [],
  dailyClosure: Array.isArray(persistedClosure) ? persistedClosure : [],
  dailyExtras:
    persistedExtras && typeof persistedExtras === "object" ? persistedExtras : {},
};

const emptyExtras = () => ({
  bozukPara: 0,
  gecisBorc: 0,
  kasaSefOdemeler: [],
});

const slice = createSlice({
  name: "zRapor",
  initialState,
  reducers: {
    addReport: {
      reducer: (state, action) => {
        state.reports.unshift(action.payload);
      },
      prepare: (data) => ({
        payload: {
          id: `Z_${nanoid(8).toUpperCase()}`,
          status: "open",
          createdAt: new Date().toISOString(),
          closedAt: null,
          visa: [],
          giderler: [],
          borclar: [],
          ...data,
        },
      }),
    },
    updateReport: (state, action) => {
      const { id, patch } = action.payload;
      const idx = state.reports.findIndex((r) => r.id === id);
      if (idx !== -1) state.reports[idx] = { ...state.reports[idx], ...patch };
    },
    deleteReport: (state, action) => {
      state.reports = state.reports.filter((r) => r.id !== action.payload);
    },

    // Nested row helpers
    addReportRow: (state, action) => {
      const { id, section, row } = action.payload;
      const r = state.reports.find((x) => x.id === id);
      if (r) r[section].push({ id: nanoid(6), ...row });
    },
    updateReportRow: (state, action) => {
      const { id, section, rowId, patch } = action.payload;
      const r = state.reports.find((x) => x.id === id);
      if (!r) return;
      const idx = r[section].findIndex((x) => x.id === rowId);
      if (idx !== -1) r[section][idx] = { ...r[section][idx], ...patch };
    },
    deleteReportRow: (state, action) => {
      const { id, section, rowId } = action.payload;
      const r = state.reports.find((x) => x.id === id);
      if (r) r[section] = r[section].filter((x) => x.id !== rowId);
    },

    // Daily extras
    setDailyExtra: (state, action) => {
      const { date, field, value } = action.payload;
      if (!state.dailyExtras[date]) state.dailyExtras[date] = emptyExtras();
      state.dailyExtras[date][field] = value;
    },
    addDailyPayment: (state, action) => {
      const { date, payment } = action.payload;
      if (!state.dailyExtras[date]) state.dailyExtras[date] = emptyExtras();
      state.dailyExtras[date].kasaSefOdemeler.push({
        id: nanoid(6),
        ...payment,
      });
    },
    updateDailyPayment: (state, action) => {
      const { date, id, patch } = action.payload;
      const ex = state.dailyExtras[date];
      if (!ex) return;
      const idx = ex.kasaSefOdemeler.findIndex((p) => p.id === id);
      if (idx !== -1)
        ex.kasaSefOdemeler[idx] = { ...ex.kasaSefOdemeler[idx], ...patch };
    },
    deleteDailyPayment: (state, action) => {
      const { date, id } = action.payload;
      const ex = state.dailyExtras[date];
      if (ex) {
        ex.kasaSefOdemeler = ex.kasaSefOdemeler.filter((p) => p.id !== id);
      }
    },

    closeDay: (state, action) => {
      const { date, successIds = [] } = action.payload;
      const closedAt = new Date().toISOString();
      state.reports = state.reports.map((r) =>
        successIds.includes(r.id) ? { ...r, status: "closed", closedAt } : r
      );
      
      const anyOpen = state.reports.some((r) => r.date === date && r.status !== "closed");
      if (!anyOpen && successIds.length > 0) {
        const exists = state.dailyClosure.some((c) => c.date === date);
        if (!exists) {
          state.dailyClosure.unshift({
            id: `DC_${nanoid(6).toUpperCase()}`,
            date,
            reportIds: successIds,
            closedAt,
            sentMock: true,
          });
        }
      }
    },
    reopenReport: (state, action) => {
      const r = state.reports.find((x) => x.id === action.payload);
      if (r) {
        r.status = "open";
        r.closedAt = null;
      }
    },
    reopenReports: (state, action) => {
      const ids = action.payload || [];
      state.reports = state.reports.map((r) =>
        ids.includes(r.id) ? { ...r, status: "open", closedAt: null } : r
      );
    },
    setReportsForDate: (state, action) => {
      const { date, backendReports } = action.payload;
      const nonClosed = state.reports.filter((r) => r.date === date && r.status !== "closed");
      const otherDates = state.reports.filter((r) => r.date !== date);
      state.reports = [...otherDates, ...nonClosed, ...backendReports];
    },
  },
});

export const {
  addReport,
  updateReport,
  deleteReport,
  addReportRow,
  updateReportRow,
  deleteReportRow,
  setDailyExtra,
  addDailyPayment,
  updateDailyPayment,
  deleteDailyPayment,
  closeDay,
  reopenReport,
  reopenReports,
  setReportsForDate,
} = slice.actions;
export default slice.reducer;
